import { fetchWithTimeout } from "./http";
import { readApiResponse } from "./apiResponse";
import { dataSourceCacheId, dataSourceGetUrl, dataSourceRequestInit, isDataSourceConfigured, type UserDataSource } from "./dataSource";

export type ItemStatus = {
  owned: boolean;
  qr: string;
  setQR: string;
};

type ItemStatusCache = {
  version: 3;
  sheetId: string;
  statuses: [id: string, owned: 0 | 1, qr: string, setQR: string][];
};

type StatusCacheStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type FetchItemStatusesOptions = {
  forceRefresh?: boolean;
  storage?: StatusCacheStorage;
};

export function normalizeFullCoordinateOwnershipId(id: string): string {
  const match = id.match(/^OAF\d+-(\d+)(?:-\d+)?$/i);
  return match ? `OAF-${match[1]}` : id;
}

type UserStatusResponse = {
  ownedIds?: unknown;
  qrs?: unknown;
};

type UserQrRow = {
  id?: unknown;
  data?: unknown;
};

const STATUS_CACHE_PREFIX = "prifetcher.itemStatuses.v3.";

function statusCacheKey(sheetId: string): string {
  return `${STATUS_CACHE_PREFIX}${sheetId}`;
}

// QR は長いビット列になるため、参考実装と同様に16進数へ縮めて保存する。
function encodeQr(value: string): string {
  if (!value || !/^[01]+$/.test(value)) return `s${value}`;
  const padded = value.padStart(Math.ceil(value.length / 4) * 4, "0");
  let hex = "";
  for (let index = 0; index < padded.length; index += 4) {
    hex += Number.parseInt(padded.slice(index, index + 4), 2).toString(16);
  }
  return `b${value.length}:${hex}`;
}

function decodeQr(value: string): string | null {
  if (value.startsWith("s")) return value.slice(1);
  const match = value.match(/^b(\d+):([0-9a-f]*)$/i);
  if (!match) return null;
  const length = Number(match[1]);
  let bits = "";
  for (const digit of match[2]) {
    bits += Number.parseInt(digit, 16).toString(2).padStart(4, "0");
  }
  if (!Number.isSafeInteger(length) || length > bits.length) return null;
  return bits.slice(bits.length - length);
}

function getDefaultStorage(): StatusCacheStorage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function loadCachedStatuses(sheetId: string, storage?: StatusCacheStorage): Map<string, ItemStatus> | null {
  if (!storage) return null;
  const key = statusCacheKey(sheetId);
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const cache = JSON.parse(raw) as Partial<ItemStatusCache>;
    if (cache.version !== 3 || cache.sheetId !== sheetId || !Array.isArray(cache.statuses)) {
      storage.removeItem(key);
      return null;
    }
    const statuses = new Map<string, ItemStatus>();
    for (const row of cache.statuses) {
      if (!Array.isArray(row) || typeof row[0] !== "string" || (row[1] !== 0 && row[1] !== 1)
        || typeof row[2] !== "string" || typeof row[3] !== "string") {
        throw new Error("invalid cache row");
      }
      const qr = decodeQr(row[2]);
      const setQR = decodeQr(row[3]);
      if (qr === null || setQR === null) throw new Error("invalid cached QR");
      statuses.set(row[0], { owned: row[1] === 1, qr, setQR });
    }
    return statuses;
  } catch {
    try {
      storage.removeItem(key);
    } catch {
      // LocalStorage が利用不能な場合も、通信による読み込みは継続する。
    }
    return null;
  }
}

function saveCachedStatuses(sheetId: string, statuses: ReadonlyMap<string, ItemStatus>, storage?: StatusCacheStorage): void {
  if (!storage) return;
  const cache: ItemStatusCache = {
    version: 3,
    sheetId,
    statuses: Array.from(statuses, ([id, status]) => [
      id,
      status.owned ? 1 : 0,
      encodeQr(status.qr),
      encodeQr(status.setQR),
    ]),
  };
  try {
    storage.setItem(statusCacheKey(sheetId), JSON.stringify(cache));
  } catch {
    // 容量超過やプライベートモードでも、取得結果自体はそのまま利用する。
  }
}

function updateCachedOwnership(
  sheetId: string,
  id: string,
  owned: boolean,
  storage?: StatusCacheStorage,
): void {
  const statuses = loadCachedStatuses(sheetId, storage);
  if (!statuses) return;
  const current = statuses.get(id);
  statuses.set(id, {
    owned,
    qr: current?.qr ?? "",
    setQR: current?.setQR ?? "",
  });
  saveCachedStatuses(sheetId, statuses, storage);
}

function updateCachedQr(sheetId: string, id: string, qr: string, storage?: StatusCacheStorage): void {
  const statuses = loadCachedStatuses(sheetId, storage);
  if (!statuses) return;
  const ids = id.includes("_") ? id.split("_") : [id];
  for (const itemId of ids) {
    const current = statuses.get(itemId);
    statuses.set(itemId, {
      owned: current?.owned ?? false,
      qr: ids.length === 1 ? qr : (current?.qr ?? ""),
      setQR: ids.length > 1 ? qr : (current?.setQR ?? ""),
    });
  }
  saveCachedStatuses(sheetId, statuses, storage);
}

function ensureStatus(statuses: Map<string, ItemStatus>, id: string): ItemStatus {
  const current = statuses.get(id);
  if (current) return current;
  const created = { owned: false, qr: "", setQR: "" };
  statuses.set(id, created);
  return created;
}

async function fetchUserStatuses(source: UserDataSource): Promise<Map<string, ItemStatus>> {
  const response = await fetchWithTimeout(
    dataSourceGetUrl(source, "status-GET"),
    dataSourceRequestInit(source),
  );
  if (!response.ok) throw new Error(`status-GET: HTTP ${response.status}`);
  const payload: UserStatusResponse = await response.json();
  if (!payload || !Array.isArray(payload.ownedIds) || !Array.isArray(payload.qrs)) {
    throw new Error("status-GET: レスポンス形式が不正です。");
  }

  const statuses = new Map<string, ItemStatus>();
  for (const rawId of payload.ownedIds) {
    const id = String(rawId ?? "").trim();
    if (id) ensureStatus(statuses, id).owned = true;
  }
  for (const rawRow of payload.qrs as UserQrRow[]) {
    const id = String(rawRow?.id ?? "").trim();
    const qr = String(rawRow?.data ?? "");
    if (!id || !qr) continue;
    const ids = id.includes("_") ? id.split("_").filter(Boolean) : [id];
    for (const itemId of ids) {
      const status = ensureStatus(statuses, itemId);
      if (ids.length > 1) status.setQR = qr;
      else status.qr = qr;
    }
  }
  return statuses;
}

export async function fetchItemStatuses(
  source: UserDataSource,
  options: FetchItemStatusesOptions = {},
): Promise<Map<string, ItemStatus>> {
  if (!isDataSourceConfigured(source)) return new Map();
  const cacheId = dataSourceCacheId(source);
  const storage = options.storage ?? getDefaultStorage();
  if (!options.forceRefresh) {
    const cached = loadCachedStatuses(cacheId, storage);
    if (cached) return cached;
  }
  const statuses = await fetchUserStatuses(source);
  saveCachedStatuses(cacheId, statuses, storage);
  return statuses;
}

export async function postItemOwnership(
  source: UserDataSource,
  id: string,
  owned: boolean,
  options: { storage?: StatusCacheStorage } = {},
): Promise<void> {
  if (!isDataSourceConfigured(source)) throw new Error("保存先が設定されていません。");
  const response = await fetchWithTimeout(
    dataSourceGetUrl(source, owned ? "check-POST" : "check-DELETE"),
    dataSourceRequestInit(source, { method: owned ? "check-POST" : "check-DELETE", id }),
  );
  await readApiResponse(response);
  updateCachedOwnership(dataSourceCacheId(source), id, owned, options.storage ?? getDefaultStorage());
}

export async function postItemQr(
  source: UserDataSource,
  id: string,
  qr: string,
  options: { storage?: StatusCacheStorage } = {},
): Promise<void> {
  if (!isDataSourceConfigured(source)) throw new Error("保存先が設定されていません。");
  if (!qr || !/^[01]+$/.test(qr)) throw new Error("読み取ったQRデータが不正です。");
  const response = await fetchWithTimeout(
    dataSourceGetUrl(source, "qr-POST"),
    dataSourceRequestInit(source, { method: "qr-POST", id, data: qr }),
  );
  await readApiResponse(response);
  updateCachedQr(dataSourceCacheId(source), id, qr, options.storage ?? getDefaultStorage());
}
